import React from 'react';
import { CheckCircle, Plus } from 'lucide-react';

const ActionPlanSection = ({ 
  actionPlan, 
  studentGradeLevel, 
  integrationStatus, 
  addActionToTimeline, 
  isIntegrating,
  calculateTimelineMessage 
}) => {
  // Handle both old array format and new urgency-based format
  const isUrgencyFormat = actionPlan && !Array.isArray(actionPlan) && (actionPlan.immediate || actionPlan.shortTerm || actionPlan.strategic);
  
  if (!actionPlan) return null;
  if (!isUrgencyFormat && !Array.isArray(actionPlan)) return null;

  const areAllActionsAdded = () => {
    return actionPlan.every(action => {
      const actionKey = `step-${action.step}`;
      return integrationStatus?.timeline?.[actionKey]?.added;
    });
  };

  const addAllActions = async () => {
    if (isIntegrating || !addActionToTimeline) return;
    
    for (const action of actionPlan) {
      const actionKey = `step-${action.step}`;
      
      if (!integrationStatus?.timeline?.[actionKey]?.added) {
        await addActionToTimeline(action);
        // Small delay to prevent overwhelming the system
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  };

  // Fallback timeline message if calculateTimelineMessage is not provided
  const getTimelineMessage = () => {
    if (calculateTimelineMessage && studentGradeLevel) {
      return calculateTimelineMessage(studentGradeLevel).primary + " - here's your game plan";
    }
    return "3.5 months to applications - here's your game plan";
  };

  // Render urgency-based format
  if (isUrgencyFormat) {
    return (
      <div className="bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-3xl p-10 shadow-xl relative overflow-hidden">
        {/* Top gradient bar */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal-400 via-pink-500 to-yellow-500"></div>
        
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-slate-900">
            {actionPlan.title || 'Your Strategic Roadmap'}
          </h2>
          <p className="text-stone-500 text-lg mt-2">
            {actionPlan.subtitle || getTimelineMessage()}
          </p>
        </div>

        {/* Urgency-based sections */}
        <div className="space-y-8">
          {/* Immediate Actions */}
          {actionPlan.immediate && (
            <div className="border-2 border-red-200 rounded-2xl p-6 bg-red-50">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{actionPlan.immediate.urgency}</span>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Immediate Actions</h3>
                  <p className="text-sm text-slate-600">{actionPlan.immediate.rationale}</p>
                </div>
              </div>
              <div className="space-y-4">
                {actionPlan.immediate.actions.map((action, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 border border-red-200">
                    <h4 className="font-bold text-slate-900 mb-2">{action.action}</h4>
                    <p className="text-sm text-slate-700 mb-2">
                      <strong>Why now:</strong> {action.why}
                    </p>
                    <p className="text-sm text-slate-600">
                      <strong>How:</strong> {action.howTo}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Short-term Actions */}
          {actionPlan.shortTerm && (
            <div className="border-2 border-yellow-200 rounded-2xl p-6 bg-yellow-50">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{actionPlan.shortTerm.urgency}</span>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Short-term Priorities</h3>
                  <p className="text-sm text-slate-600">{actionPlan.shortTerm.rationale}</p>
                </div>
              </div>
              <div className="space-y-4">
                {actionPlan.shortTerm.actions.map((action, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 border border-yellow-200">
                    <h4 className="font-bold text-slate-900 mb-2">{action.action}</h4>
                    <p className="text-sm text-slate-700 mb-2">
                      <strong>Impact:</strong> {action.why}
                    </p>
                    <p className="text-sm text-slate-600">
                      <strong>Steps:</strong> {action.howTo}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strategic Actions */}
          {actionPlan.strategic && (
            <div className="border-2 border-emerald-200 rounded-2xl p-6 bg-emerald-50">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{actionPlan.strategic.urgency}</span>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Strategic Positioning</h3>
                  <p className="text-sm text-slate-600">{actionPlan.strategic.rationale}</p>
                </div>
              </div>
              <div className="space-y-4">
                {actionPlan.strategic.actions.map((action, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 border border-emerald-200">
                    <h4 className="font-bold text-slate-900 mb-2">{action.action}</h4>
                    <p className="text-sm text-slate-700 mb-2">
                      <strong>Long-term value:</strong> {action.why}
                    </p>
                    <p className="text-sm text-slate-600">
                      <strong>Approach:</strong> {action.howTo}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Original array-based format (backward compatibility)
  return (
    <div className="bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-3xl p-10 shadow-xl relative overflow-hidden">
      {/* Top gradient bar */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal-400 via-pink-500 to-yellow-500"></div>
      
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-4 mb-4">
          <h2 className="text-3xl font-bold text-slate-900">Your Next Steps</h2>
          {addActionToTimeline && (
            <button
              onClick={addAllActions}
              disabled={isIntegrating || areAllActionsAdded()}
              className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors ${
                areAllActionsAdded()
                  ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              {areAllActionsAdded() ? '✓ All Added to Timeline' : 'Add All to Timeline'}
            </button>
          )}
        </div>
        <p className="text-stone-500 text-lg">
          {getTimelineMessage()}
        </p>
      </div>

      {/* Action Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {actionPlan.map((action, index) => {
          const actionKey = `step-${action.step}`;
          const isAdded = integrationStatus?.timeline?.[actionKey]?.added;
          
          return (
            <div key={index} className="bg-stone-50 border border-stone-200 rounded-2xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">
                    {action.step}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{action.title}</h3>
                </div>
                {addActionToTimeline && (
                  <button
                    onClick={() => addActionToTimeline(action)}
                    disabled={isIntegrating || isAdded}
                    className={`text-xs px-2 py-1 rounded font-medium transition-colors flex items-center gap-1 ${
                      isAdded
                        ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
                        : 'bg-azure-100 hover:bg-azure-200 text-azure-700'
                    }`}
                  >
                    {isIntegrating ? (
                      <div className="w-3 h-3 border border-azure-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : isAdded ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Added
                      </>
                    ) : (
                      <>
                        <Plus className="w-3 h-3" />
                        Add
                      </>
                    )}
                  </button>
                )}
              </div>
              
              <p className="text-slate-700 text-sm leading-relaxed mb-4">
                {action.description}
              </p>
              
              <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-2">
                <p className="text-teal-700 text-xs font-semibold">
                  ⏱️ {action.timeline}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActionPlanSection;